import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormGroup, FormGroupLabel, ErrorMessage, RequiredMark } from './BaseInput';
// import { colorFieldsBorder, colorBackground, colorText } from '../../theme/palette';
import { Button } from '../Button';
import Icon from '../Icon';
import { colorAccent, colorBackground, colorBorder, colorGray, colorFieldsBorder, colorText } from '../../theme';


const SelectWithModal = ({
  name,
  label,
  placeholder = 'Clique no botao para selecionar',
  value,
  onChange,
  disabled = false,
  required = false,
  error,
  input,
  meta,
  modalComponent: ModalComponent,
  modalProps = {},
  extraButtons = [],
  ...props
}) => {
  // Usar input.value e input.onChange se disponivel (React Final Form)
  const fieldValue = input ? input.value : value;
  const fieldOnChange = input ? input.onChange : onChange;
  const fieldName = input ? input.name : name;

  const [showModal, setShowModal] = useState(false);

  // Extrair label do valor (se for objeto {value, label})
  const displayValue = fieldValue
    ? (typeof fieldValue === 'object' && fieldValue.label ? fieldValue.label : fieldValue)
    : '';

  const handleOpenModal = () => {
    if (!disabled) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelect = (selectedItem) => {
    if (fieldOnChange) {
      fieldOnChange(selectedItem);
    }
    setShowModal(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (fieldOnChange && !disabled && !required) {
      fieldOnChange(null);
    }
  };

  return (
    <>
      <FormGroup>
        {label && (
          <FormGroupLabel>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </FormGroupLabel>
        )}

        <OuterWrapper>
          <InputWrapper>
            <StyledInput
              type="text"
              value={displayValue}
              placeholder={placeholder}
              disabled={disabled}
              readOnly
              onClick={handleOpenModal}
              hasValue={!!displayValue}
            />

            <ButtonGroup>
              {displayValue && !required && !disabled && (
                <ClearButton
                  type="button"
                  onClick={handleClear}
                  disabled={disabled}
                  title="Limpar selecao"
                >
                  <Icon name="close-circle-outline" size={18} />
                </ClearButton>
              )}

              <SelectButton
                type="button"
                onClick={handleOpenModal}
                disabled={disabled}
                title="Abrir selecao"
              >
                <Icon name="search" size={20} />
              </SelectButton>
            </ButtonGroup>
          </InputWrapper>

          {Array.isArray(extraButtons) && extraButtons.length > 0 && (
            <ExternalButtons>
              {extraButtons.map((button) => {
                const isDisabled = disabled || button.disabled || !fieldValue;
                return (
                <ExtraButton
                  key={button.id || button.title}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isDisabled && typeof button.onClick === 'function') {
                      button.onClick(fieldValue);
                    }
                  }}
                  disabled={isDisabled}
                  title={button.title}
                >
                  <Icon name={button.icon || 'map-marker-path'} size={18} />
                </ExtraButton>
              );
              })}
            </ExternalButtons>
          )}
        </OuterWrapper>

        {(error || (meta && meta.touched && meta.error)) && (
          <ErrorMessage>
            {error || meta.error}
          </ErrorMessage>
        )}
      </FormGroup>

      {ModalComponent && (
        <ModalComponent
          show={showModal}
          onHide={handleCloseModal}
          onSelect={handleSelect}
          currentValue={fieldValue}
          {...modalProps}
        />
      )}
    </>
  );
};

SelectWithModal.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  modalComponent: PropTypes.elementType.isRequired,
  modalProps: PropTypes.object,
  extraButtons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
  })),
};

// Styled Components
const OuterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 50px 0 10px;
  border: 1px solid ${colorFieldsBorder};
  border-radius: 0;
  background-color: ${props => props.disabled ? '#f5f5f5' : colorBackground};
  color: ${colorText};
  font-size: 14px;
  line-height: 30px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${colorAccent};
  }

  &:focus {
    outline: none;
    border-color: ${colorAccent};
    box-shadow: none;
  }

  &::placeholder {
    color: ${colorGray};
  }
`;

const ButtonGroup = styled.div`
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 2px;
  align-items: center;
`;

const ExternalButtons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 2px;
  background-color: transparent;
  color: #999;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #fee;
    color: #e74c3c;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    display: block;
  }
`;

const ExtraButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${colorBorder};
  border-radius: 2px;
  background-color: ${colorBackground};
  color: ${colorText};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${colorAccent};
    color: ${colorAccent};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    display: block;
  }
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 2px;
  background-color: ${colorAccent};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${colorAccent};
  }

  &:active:not(:disabled) {
    background-color: ${colorAccent};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: ${colorGray};
  }

  svg {
    display: block;
  }
`;

export default SelectWithModal;
